import React from 'react';

function GalleryEditor({ pageContent, setPageContent, uploading, uploadingField, handleRowImageUpload, handleImageUpload, deleteImageFromFirebase, addTableRow, deleteTableRow, updateTableRow }) {

  const handlePhotoUpload = async (photoId, file) => {
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('❌ Only image files are allowed.');
      return;
    }
    
    try {
      const { uploadImage } = await import('../../firebase/firebase-config');
      const url = await uploadImage(file, `gallery/photos/${photoId}_${Date.now()}`);
      
      const newPhotos = (pageContent.photos || []).map(photo => 
        photo.id === photoId ? { ...photo, url: url } : photo
      );
      setPageContent({ ...pageContent, photos: newPhotos });
      alert('✅ Photo uploaded successfully!');
    } catch (error) {
      alert('❌ Error uploading photo: ' + error.message);
    }
  };

  const handleDeletePhoto = async (photoId, photoUrl) => {
    if (!photoUrl) return;
    
    if (window.confirm('Delete this photo permanently?')) {
      try {
        const { deleteImageFromStorage } = await import('../../firebase/firebase-config');
        await deleteImageFromStorage(photoUrl);
        
        const newPhotos = (pageContent.photos || []).map(photo => 
          photo.id === photoId ? { ...photo, url: null } : photo
        );
        setPageContent({ ...pageContent, photos: newPhotos });
        alert('✅ Photo deleted successfully!');
      } catch (error) {
        alert('❌ Error deleting photo: ' + error.message);
      }
    }
  };

  const deletePhotoRow = (id) => {
    if (window.confirm('Delete this photo entry?')) {
      const newPhotos = (pageContent.photos || []).filter(photo => photo.id !== id);
      setPageContent({ ...pageContent, photos: newPhotos });
    }
  };

  const deleteVideoRow = (id) => {
    if (window.confirm('Delete this video entry?')) {
      const newVideos = (pageContent.youtubeVideos || []).filter(video => video.id !== id);
      setPageContent({ ...pageContent, youtubeVideos: newVideos });
    }
  };

  return (
    <div className="page-editor">
      <div className="editor-card">
        <h3>🖼️ Gallery Manager</h3>
        <p className="editor-description">Manage photos, YouTube videos, and Instagram feed for the gallery page.</p>

        <div className="form-group">
          <label>Page Title</label>
          <input 
            value={pageContent.title || 'Photo Gallery & Videos'} 
            onChange={(e) => setPageContent({...pageContent, title: e.target.value})} 
            className="form-input" 
          />
        </div>

        {/* Photos Section */}
        <div className="form-card">
          <div className="section-header">
            <h4>📷 Photos</h4>
            <button className="add-btn" onClick={() => addTableRow('photos', { title: '', caption: '', url: '' })}>+ Add Photo</button>
          </div>
          
          {(pageContent.photos || []).map((photo, idx) => (
            <div key={photo.id} className="photo-editor-item">
              <div className="photo-editor-header">
                <span className="photo-number">Photo {idx + 1}</span>
                <button className="remove-btn" onClick={() => deletePhotoRow(photo.id)}>🗑️ Delete Photo</button>
              </div>
              <div className="photo-editor-fields">
                <input 
                  placeholder="Photo Title" 
                  value={photo.title || ''} 
                  onChange={(e) => updateTableRow('photos', photo.id, 'title', e.target.value)} 
                  className="form-input" 
                />
                <input 
                  placeholder="Caption" 
                  value={photo.caption || ''} 
                  onChange={(e) => updateTableRow('photos', photo.id, 'caption', e.target.value)} 
                  className="form-input" 
                />
              </div>
              {photo.url && (
                <div className="photo-preview">
                  <img src={photo.url} alt={photo.title} />
                  <button className="remove-btn-small" onClick={() => handleDeletePhoto(photo.id, photo.url)}>🗑️ Remove Image</button>
                </div>
              )}
              <label className="upload-label">
                {uploading && uploadingField === `photo_${photo.id}` ? '⏳ Uploading...' : '📷 Upload Photo'}
                <input 
                  type="file" 
                  accept="image/*" 
                  style={{display: 'none'}} 
                  onChange={(e) => { if (e.target.files[0]) handlePhotoUpload(photo.id, e.target.files[0]); }} 
                />
              </label>
            </div>
          ))}
          {(!pageContent.photos || pageContent.photos.length === 0) && (
            <div className="empty-message">No photos added. Click "Add Photo" to upload.</div>
          )}
        </div>

        {/* YouTube Videos Section */}
        <div className="form-card">
          <div className="section-header">
            <h4>🎬 YouTube Videos</h4>
            <button className="add-btn" onClick={() => addTableRow('youtubeVideos', { title: '', url: '' })}>+ Add Video</button>
          </div>
          
          {(pageContent.youtubeVideos || []).map((video, idx) => (
            <div key={video.id} className="video-editor-item">
              <div className="video-editor-header">
                <span className="video-number">Video {idx + 1}</span>
                <button className="remove-btn" onClick={() => deleteVideoRow(video.id)}>🗑️ Delete Video</button>
              </div>
              <input 
                placeholder="Video Title" 
                value={video.title || ''} 
                onChange={(e) => updateTableRow('youtubeVideos', video.id, 'title', e.target.value)} 
                className="form-input" 
              />
              <input 
                placeholder="YouTube URL (e.g., https://youtu.be/... or https://youtube.com/watch?v=...)" 
                value={video.url || ''} 
                onChange={(e) => updateTableRow('youtubeVideos', video.id, 'url', e.target.value)} 
                className="form-input" 
              />
              {video.url && (
                <div className="video-url-preview">
                  <small>Preview: {video.url}</small>
                </div>
              )}
            </div>
          ))}
          {(!pageContent.youtubeVideos || pageContent.youtubeVideos.length === 0) && (
            <div className="empty-message">No videos added. Click "Add Video" to add YouTube links.</div>
          )}
        </div>

        {/* Instagram Feed Section */}
        <div className="form-card">
          <div className="section-header">
            <h4>📸 Instagram Feed</h4>
          </div>
          <div className="form-group">
            <label>Instagram Embed URL</label>
            <textarea 
              value={pageContent.instagramEmbed || ''} 
              onChange={(e) => setPageContent({...pageContent, instagramEmbed: e.target.value})} 
              className="form-input" 
              rows="3"
              placeholder="Paste Instagram embed code or URL here..."
            />
            <small style={{display: 'block', marginTop: 8, color: '#64748b'}}>
              You can get embed code from Instagram posts (Share → Embed)
            </small>
          </div>
          <div className="form-group">
            <label>Preview</label>
            <div style={{background: '#f8fafc', padding: 16, borderRadius: 8, fontSize: 14}}>
              <p><strong>📷 Photos:</strong> {(pageContent.photos || []).length} photos</p>
              <p><strong>🎬 Videos:</strong> {(pageContent.youtubeVideos || []).length} videos</p>
              <p><strong>📸 Instagram:</strong> {pageContent.instagramEmbed ? 'Configured ✓' : 'Not configured'}</p>
            </div>
          </div>
        </div>

        <div className="info-box">
          <div className="info-icon">💡</div>
          <div className="info-content">
            <strong>Tips:</strong>
            <ul>
              <li>Photos will appear in a responsive grid layout</li>
              <li>Click on photos to view in fullscreen lightbox</li>
              <li>YouTube videos will be embedded automatically</li>
              <li>Supported YouTube URL formats: youtu.be/... or youtube.com/watch?v=...</li>
              <li>Instagram embed requires the official embed code from Instagram</li>
            </ul>
          </div>
        </div>
      </div>

      <style>{`
        .editor-description {
          color: #64748b;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .form-card {
          background: #f8fafc;
          padding: 20px;
          border-radius: 16px;
          margin-bottom: 25px;
          border: 1px solid #e2e8f0;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .section-header h4 {
          margin: 0;
          color: #1a3a6b;
          font-size: 18px;
        }

        .photo-editor-item, .video-editor-item {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 15px;
        }

        .photo-editor-header, .video-editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .photo-number, .video-number {
          font-weight: 600;
          color: #1a3a6b;
        }

        .photo-editor-fields {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 12px;
        }

        .photo-preview {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px;
          background: #f1f5f9;
          border-radius: 8px;
          margin-bottom: 12px;
        }

        .photo-preview img {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
        }

        .video-url-preview {
          margin-top: 8px;
          padding: 8px;
          background: #f1f5f9;
          border-radius: 6px;
        }

        .remove-btn-small {
          background: #ef4444;
          color: white;
          padding: 4px 12px;
          border-radius: 6px;
          border: none;
          font-size: 12px;
          cursor: pointer;
        }

        .empty-message {
          text-align: center;
          padding: 30px;
          color: #94a3b8;
          font-style: italic;
        }

        .info-box {
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 12px;
          margin-top: 20px;
          display: flex;
          gap: 12px;
        }

        .info-icon {
          font-size: 20px;
        }

        .info-content {
          flex: 1;
        }

        .info-content ul {
          margin: 8px 0 0 20px;
          color: #64748b;
          font-size: 13px;
        }

        @media (max-width: 768px) {
          .photo-editor-fields {
            grid-template-columns: 1fr;
          }
          
          .section-header {
            flex-direction: column;
            text-align: center;
          }
          
          .photo-preview {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}

export default GalleryEditor;